import { useCallback, useEffect, useRef } from "react";

import type { CurriculumAudioPlayer } from "./useCurriculumAudioPlayer";
import type { CurriculumFeedbackPlayback } from "./useCurriculumFeedbackAudio";

interface ActiveCurriculumVoiceSequence {
  initialVoiceId: string | null;
  readonly items: readonly CurriculumFeedbackPlayback[];
  readonly key: string;
  index: number;
  observedActiveVoice: boolean;
  phase: "playing" | "starting";
}

function getSequenceKey(items: readonly CurriculumFeedbackPlayback[]): string {
  return JSON.stringify(
    items.map(({ audioPath, playbackRequest }) => [audioPath, playbackRequest])
  );
}

export function useCurriculumVoiceSequence(
  player: CurriculumAudioPlayer,
  requestedItems: readonly (CurriculumFeedbackPlayback | null)[],
  enabled = true
): void {
  const { activeVoiceId, playPath } = player;
  const activeSequenceRef = useRef<ActiveCurriculumVoiceSequence | null>(null);
  const completedSequenceKeyRef = useRef<string | null>(null);
  const activeVoiceIdRef = useRef(activeVoiceId);
  const sequenceItems = requestedItems.filter(
    (item): item is CurriculumFeedbackPlayback => item !== null
  );
  const sequenceKey =
    enabled && sequenceItems.length > 0 ? getSequenceKey(sequenceItems) : null;

  useEffect(() => {
    activeVoiceIdRef.current = activeVoiceId;
  }, [activeVoiceId]);

  const startCurrentItem = useCallback(
    async (sequence: ActiveCurriculumVoiceSequence): Promise<void> => {
      const item = sequence.items[sequence.index];
      if (item === undefined) {
        activeSequenceRef.current = null;
        completedSequenceKeyRef.current = sequence.key;
        return;
      }

      sequence.initialVoiceId = activeVoiceIdRef.current;
      sequence.observedActiveVoice = false;
      sequence.phase = "starting";

      const started = await playPath(item.audioPath);
      if (activeSequenceRef.current !== sequence) {
        return;
      }

      if (!started) {
        activeSequenceRef.current = null;
        completedSequenceKeyRef.current = sequence.key;
        return;
      }

      sequence.phase = "playing";
      sequence.observedActiveVoice =
        activeVoiceIdRef.current === item.audioPath;
    },
    [playPath]
  );

  useEffect(() => {
    if (sequenceKey === null) {
      activeSequenceRef.current = null;
      completedSequenceKeyRef.current = null;
      return;
    }

    if (
      activeSequenceRef.current !== null &&
      activeSequenceRef.current.key !== sequenceKey
    ) {
      activeSequenceRef.current = null;
    }

    if (
      activeSequenceRef.current?.key === sequenceKey ||
      completedSequenceKeyRef.current === sequenceKey
    ) {
      return;
    }

    const sequence: ActiveCurriculumVoiceSequence = {
      initialVoiceId: activeVoiceIdRef.current,
      index: 0,
      items: sequenceItems,
      key: sequenceKey,
      observedActiveVoice: false,
      phase: "starting"
    };
    activeSequenceRef.current = sequence;
    void startCurrentItem(sequence);
  }, [sequenceItems, sequenceKey, startCurrentItem]);

  useEffect(() => {
    const sequence = activeSequenceRef.current;
    if (sequence === null || sequence.phase !== "playing") {
      return;
    }

    const item = sequence.items[sequence.index];
    if (item === undefined) {
      activeSequenceRef.current = null;
      completedSequenceKeyRef.current = sequence.key;
      return;
    }

    if (activeVoiceId === item.audioPath) {
      sequence.observedActiveVoice = true;
      return;
    }

    if (!sequence.observedActiveVoice) {
      if (activeVoiceId !== null && activeVoiceId !== sequence.initialVoiceId) {
        activeSequenceRef.current = null;
        completedSequenceKeyRef.current = sequence.key;
      }
      return;
    }

    if (activeVoiceId !== null) {
      activeSequenceRef.current = null;
      completedSequenceKeyRef.current = sequence.key;
      return;
    }

    sequence.index += 1;
    if (sequence.index >= sequence.items.length) {
      activeSequenceRef.current = null;
      completedSequenceKeyRef.current = sequence.key;
      return;
    }

    void startCurrentItem(sequence);
  }, [activeVoiceId, startCurrentItem]);
}
